import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  input,
  InputSignal,
  linkedSignal,
  model,
  resource,
  signal,
  viewChild
} from '@angular/core';
import { FilterInputBase } from '../filter-input.base';
import { FilterItemConfig } from '../../../../types/filter-config';
import { filterValuesEqual } from '../../../../helper/filter-value-equals.function';
import {
  QueryColumnFilter,
  QueryFilter,
  QueryNumberSetFilter,
  QueryStringSetFilter,
  uuid
} from '@softline/core';
import { JsonPipe } from '@angular/common';
import { ButtonComponent, EmptyStateComponent, IsDefinedNotEmptyPipe, LoadingSkeletonComponent, LoadingSpinnerComponent, SearchInputComponent, ToggleComponent, ToggleGroupComponent, TranslatePipe, translator } from '@softline/ui-core';
import { SelectDsHost, SelectDsOptionComponent, SelectFilterMode } from '@softline/ui-core/select';
import { BehaviorSubject, debounceTime } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { Filter } from '../../../../types/filter';
import { PageFilterDataService } from '../../../services/data-source/page-filter-data.service';
import { PAGE_FILTER_VALUES_PAGE_SIZE, pagedFilterValues } from '../../../helper/paged-filter-values.function';

export const ITEMS_PER_PAGE = PAGE_FILTER_VALUES_PAGE_SIZE;

/** A finger as the primary pointer: focusing a text field would open the on-screen keyboard. */
function hasCoarsePointer(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

@Component({
  selector: 'soft-multi-select-filter',
  imports: [
    ToggleGroupComponent,
    ToggleComponent,
    LoadingSpinnerComponent,
    SelectDsOptionComponent,
    SearchInputComponent,
    EmptyStateComponent,
    ButtonComponent,
    TranslatePipe,
    LoadingSkeletonComponent,
    IsDefinedNotEmptyPipe
  ],
  templateUrl: './multi-select-filter-input.component.html',
  styleUrl: './multi-select-filter-input.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {provide: FilterInputBase, useExisting: forwardRef(() => MultiSelectFilterInputComponent)},
    // the sidebar list renders the design-system option, so it looks and behaves exactly
    // like the dropdown's list instead of a hand-rolled row
    {provide: SelectDsHost, useExisting: forwardRef(() => MultiSelectFilterInputComponent)}
  ]
})
export class MultiSelectFilterInputComponent<T extends object, TValue>
  extends FilterInputBase<T, 'in' | 'notIn'>
  implements SelectDsHost<TValue>
{
  private t = translator();

  // SelectDsHost - the option reads its selected/visible state from here
  readonly listboxId = signal(`page-filter-picker-${uuid()}`);
  readonly isMulti = true;
  readonly searchable = signal(true);
  /** values arrive already filtered from the backend, so the option never hides itself */
  readonly filterMode = signal<SelectFilterMode>('external');
  readonly groupSelectedFirst = signal(false);
  readonly showSelectedChips = signal(false);

  isOptionSelected(value: TValue): boolean {
    return this.isSelected(value);
  }

  selectOption(value: TValue): void {
    this.toggleValue(value);
  }

  readonly page = signal<number>(0);

  readonly search$ = new BehaviorSubject<string>('');
  readonly debouncedSearch$ = this.search$.pipe(debounceTime(300));
  readonly search = toSignal(this.debouncedSearch$, {initialValue: ''});

  override readonly item: InputSignal<FilterItemConfig<T>> = input.required<FilterItemConfig<T>>();
  override readonly itemFilter = input.required<Filter<T> & {operator: 'in' | 'notIn'} | null>();
  override readonly operator = input.required<'in' | 'notIn'>();
  override readonly dataService = input.required<PageFilterDataService<T>>();
  override readonly queryFilter = input.required<QueryFilter<T> | null>();

  readonly loadingRows = signal<number[]>([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);

  readonly selectedView = model<readonly string[]>([]);
  readonly onlySelected = computed(() => this.selectedView().length > 0);

  onOnlySelectedChange(value: unknown): void {
    this.selectedView.set(Array.isArray(value) && value.length > 0 ? ['selected'] : []);
  }
  readonly selectedCount = computed(() => this.selectedValues().length);
  readonly onlySelectedLabel = computed(() => {
    const label = this.t('#CORE_FILTER.MULTI_SELECT.ONLY_SELECTED')();
    const count = this.selectedCount();
    return count > 0 ? `${label} (${count})` : label;
  });

  selectedValues = linkedSignal(() => {
    const filter = this.itemFilter();
    if(!filter)
      return [];
    return filter.value
  })

  // resource()'s own `equal` only gates its resolved value, not whether the loader re-fires -
  // that's decided by comparing the params() result via ===. Memoize it here so an equal-but-
  // new-reference params object (e.g. from a parent that rebuilds queryFilter on every CD
  // cycle) doesn't re-trigger the loader.
  private static readonly requestParamsEqual = <T extends object>(
    a: { item: FilterItemConfig<T>; filter: QueryFilter<T> | null; page: number; search: string },
    b: { item: FilterItemConfig<T>; filter: QueryFilter<T> | null; page: number; search: string }
  ): boolean =>
    a.item === b.item &&
    a.page === b.page &&
    a.search === b.search &&
    JSON.stringify(a.filter) === JSON.stringify(b.filter);

  private readonly filterKey = computed(() => JSON.stringify(this.queryFilter()));

  private readonly requestParams = computed(
    () => ({item: this.item(), filter: this.queryFilter(), page: this.page(), search: this.search()}),
    {equal: MultiSelectFilterInputComponent.requestParamsEqual}
  );

  // the paging, the failure handling and the empty-window walk are shared with the pinned dropdown
  private readonly paged = pagedFilterValues<TValue, ReturnType<typeof this.requestParams>>({
    request: this.requestParams,
    page: this.page,
    load: (request, start, end) =>
      this.dataService().loadItems(request.item, request.filter, start, end, request.search),
  });
  readonly values = this.paged.values;
  readonly hasMore = this.paged.hasMore;
  readonly loading = this.paged.loading;
  readonly loadFailed = this.paged.failed;
  readonly loadErrorDetail = this.paged.errorDetail;

  readonly searchInput = viewChild(SearchInputComponent);
  /** what the field shows: `search` is debounced and drives the request, this is not */
  readonly searchText = signal('');
  readonly pickerList = viewChild<ElementRef<HTMLElement>>('pickerList');

  // Index of the keyboard-highlighted option within the currently displayed
  // list; -1 means nothing is highlighted yet.
  readonly highlightedIndex = signal<number>(-1);

  // The list the keyboard navigation operates on, matching what is rendered.
  readonly displayedValues = computed<TValue[]>(() =>
    this.onlySelected() ? this.selectedForDisplay() : this.values()
  );

  // Selected values narrowed by the search term: the toggle and the search box compose
  // instead of the toggle silently winning, which is what the old tab did.
  readonly selectedForDisplay = computed<TValue[]>(() => {
    const term = this.searchText().trim().toLowerCase();
    const values = this.selectedValues();
    if (!term) return values;
    const format = this.item().formatter as (value: TValue) => string;
    return values.filter((value) => (format(value) ?? '').toLowerCase().includes(term));
  });

  readonly nothingSelected = computed(
    () => this.onlySelected() && this.selectedCount() === 0
  );

  constructor() {
    super();
    // Whenever the query (search or filters) changes, restart pagination
    // from the first page and drop the accumulated windows.
    effect(() => {
      this.search();
      this.filterKey();
      this.page.set(0);
    });
    // Reset the keyboard highlight whenever the visible list changes
    // (typing a search or switching tabs).
    effect(() => {
      this.search();
      this.onlySelected();
      this.highlightedIndex.set(-1);
    });
    // Focus the search field on open so the user can type immediately - but not on a
    // touch device, where focus summons the on-screen keyboard and it hides the end of
    // the list together with the footer.
    afterNextRender(() => {
      if (hasCoarsePointer()) return;
      this.searchInput()?.focus();
    });
  }

  retry(): void {
    this.paged.retry();
  }

  /** continue past the guard that stopped the walk through windows that render nothing */
  loadMorePages(): void {
    this.paged.resume();
  }

  isSelected(value: TValue): boolean {
    return this.selectedValues().some((v) => filterValuesEqual(this.item(), v, value));
  }

  toggleValue(value: TValue): void {
    const currentValue = this.selectedValues().find((v) => filterValuesEqual(this.item(), v, value));
    if(currentValue !== undefined)
      this.selectedValues.update((prev) => prev.filter((v) => v !== currentValue));
    else
      this.selectedValues.update((prev) => [...prev, value]);
  }

  onSearchValue(value: string | null): void {
    this.searchText.set(value ?? '');
    this.search$.next(value ?? '');
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape': {
        if (this.searchText()) {
          event.preventDefault();
          // the overlay closes on any Escape reaching the document - this one only clears the search
          event.stopPropagation();
          this.onSearchValue('');
          this.highlightedIndex.set(-1);
        }
        break;
      }
      case 'Enter': {
        event.preventDefault();
        const list = this.displayedValues();
        const index = this.highlightedIndex();
        const value =
          index >= 0 && index < list.length ? list[index] : list[0];
        if (value !== undefined)
          this.toggleValue(value);
        break;
      }
      case 'ArrowDown':
        event.preventDefault();
        this.moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveHighlight(-1);
        break;
    }
  }

  private moveHighlight(delta: number): void {
    const list = this.displayedValues();
    if (list.length === 0) {
      this.highlightedIndex.set(-1);
      return;
    }
    const next = Math.min(
      Math.max(this.highlightedIndex() + delta, 0),
      list.length - 1
    );
    this.highlightedIndex.set(next);
    this.scrollHighlightedIntoView(next);
  }

  private scrollHighlightedIntoView(index: number): void {
    const container = this.pickerList()?.nativeElement;
    const el = container?.querySelector<HTMLElement>(
      `[data-option-index="${index}"]`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (this.onlySelected())
      return;
    if (this.loading())
      return;
    if (!this.hasMore())
      return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      this.page.set(this.page() + 1);
    }
  }

  override getFilterValue(): Filter<T> | null {
    const item = this.item();
    const selectedValues = this.selectedValues();

    if(selectedValues.length === 0)
      return null;

    return {
      uuid: uuid(),
      field: item.field,
      type: item.type as any,
      operator: this.operator(),
      value: selectedValues
    }
  }
}
